/**
 * The bag's rules (ADR-091 decision 4): the only code that changes a bag, on the page for display and on the map
 * server for truth. A bag is two integer arrays of one length, by slot: the item's number (its place in the items table
 * counting from 1, 0 for an empty slot) and its quantity (0 for an empty slot). Every function takes plain data, never
 * changes it, and returns a bag of its own or a refusal, allocating nothing beyond its result.
 *
 * A slot at or past the bag's size and a stack above the item's stack size are kept as they are and stay usable (ADR-091
 * decision 8): adding never tops either up and never fills an empty slot past the size, and a move never lands an item
 * on one, neither as its target nor as the other half of a swap.
 */

import type { Item, ItemKind, ItemRarity } from "../rules/types.ts";

/**
 * The slots a bag holds (the author's ruling 1). The page reads a bag's size from the `bag` message, never from this.
 */
export const BAG_SLOTS = 20;

/**
 * A bag: each slot's item number and quantity, the two arrays of one length. Slots past either array's end read as
 * empty.
 */
export interface BagContents {
  readonly items: readonly number[];
  readonly quantities: readonly number[];
}

/**
 * A bag as the rules read one: anything indexed by slot with a length, so a plain array and a typed array both serve.
 * The map server's mirror is two typed arrays (ADR-091 decision 6), read here without a copy.
 */
export interface BagView {
  readonly items: ArrayLike<number>;
  readonly quantities: ArrayLike<number>;
}

/**
 * A change the bag allowed: the bag after it and the slots it changed, in the order it changed them.
 */
export interface BagChange {
  readonly ok: true;
  readonly bag: BagContents;
  readonly slots: readonly number[];
}

/**
 * Why the bag refused a change: `full` for an amount that does not fit or a merge onto a full stack, `empty` for a slot
 * that holds nothing or fewer units than asked, `range` for a slot outside the bag, `same` for a move onto its own slot.
 */
export type BagRefusalReason = "full" | "empty" | "range" | "same";

/**
 * A change the bag refused; nothing changed.
 */
export interface BagRefusal {
  readonly ok: false;
  readonly reason: BagRefusalReason;
}

/**
 * What a change to a bag answers.
 */
export type BagResult = BagChange | BagRefusal;

/**
 * An empty bag.
 *
 * @param size Its slots, `BAG_SLOTS` unless given.
 * @returns Two arrays of zeros.
 * @throws RangeError when the size is not a whole number of at least 1.
 */
export function emptyBag(size = BAG_SLOTS): BagContents {
  requireCount(size, "size");
  return { items: new Array<number>(size).fill(0), quantities: new Array<number>(size).fill(0) };
}

/**
 * Throws unless a value is a whole number of at least 1.
 *
 * @param value The value.
 * @param what What it is, for the message.
 * @throws RangeError when it is not.
 */
function requireCount(value: number, what: string): void {
  if (!Number.isInteger(value) || value < 1) {
    throw new RangeError(`${what} is a whole number of at least 1, not ${value}`);
  }
}

/**
 * A copy of a bag's arrays, at least `length` long, slots past the source's end empty.
 *
 * @param bag The bag.
 * @param length The least length.
 * @returns The two arrays, safe to change.
 */
function copyOf(bag: BagView, length: number): { items: number[]; quantities: number[] } {
  const size = Math.max(length, bag.items.length, bag.quantities.length);
  const items = new Array<number>(size);
  const quantities = new Array<number>(size);
  for (let slot = 0; slot < size; slot += 1) {
    const item = bag.items[slot] ?? 0;
    items[slot] = item;
    quantities[slot] = item === 0 ? 0 : (bag.quantities[slot] ?? 0);
  }
  return { items, quantities };
}

/**
 * Whether a slot number is a whole number inside a range.
 *
 * @param slot The slot.
 * @param end The first slot past the range.
 * @returns True when it is.
 */
function slotIn(slot: number, end: number): boolean {
  return Number.isInteger(slot) && slot >= 0 && slot < end;
}

/**
 * Adds units of an item, all or nothing: first to the stacks of that item inside the bag's size that have room, in slot
 * order, then to its empty slots, in slot order.
 *
 * @param bag The bag.
 * @param item The item's number, 1 or more.
 * @param count The units to add, 1 or more.
 * @param stack The item's stack size, 1 or more.
 * @param size The bag's size, `BAG_SLOTS` unless given.
 * @returns The bag with every unit added and the slots changed, or `full` with nothing added.
 * @throws RangeError when the item, the count, the stack or the size is not a whole number of at least 1.
 * @example
 * addToBag({ items: [1, 0], quantities: [19, 0] }, 1, 3, 20, 2);
 * // { ok: true, bag: { items: [1, 1], quantities: [20, 2] }, slots: [0, 1] }
 */
export function addToBag(bag: BagView, item: number, count: number, stack: number, size = BAG_SLOTS): BagResult {
  requireCount(item, "item");
  requireCount(count, "count");
  requireCount(stack, "stack");
  requireCount(size, "size");
  let room = 0;
  for (let slot = 0; slot < size && room < count; slot += 1) {
    const held = bag.items[slot] ?? 0;
    if (held === 0) {
      room += stack;
    } else if (held === item) {
      room += Math.max(0, stack - (bag.quantities[slot] ?? 0));
    }
  }
  if (room < count) {
    return { ok: false, reason: "full" };
  }
  const next = copyOf(bag, size);
  const slots: number[] = [];
  let left = count;
  for (let slot = 0; slot < size && left > 0; slot += 1) {
    const quantity = next.quantities[slot] as number;
    if (next.items[slot] === item && quantity < stack) {
      const moved = Math.min(left, stack - quantity);
      next.quantities[slot] = quantity + moved;
      left -= moved;
      slots.push(slot);
    }
  }
  for (let slot = 0; slot < size && left > 0; slot += 1) {
    if (next.items[slot] === 0) {
      const moved = Math.min(left, stack);
      next.items[slot] = item;
      next.quantities[slot] = moved;
      left -= moved;
      slots.push(slot);
    }
  }
  return { ok: true, bag: next, slots };
}

/**
 * Takes units from one slot, emptying it at 0. A slot past the bag's size that the bag's arrays hold may be taken
 * from: it is kept and usable.
 *
 * @param bag The bag.
 * @param slot The slot.
 * @param count The units to take, 1 unless given.
 * @param size The bag's size, `BAG_SLOTS` unless given.
 * @returns The bag with the units taken and the slot changed, `range` for a slot that is no whole number from 0 to
 *   one less than the size or the arrays' length, whichever is greater, or `empty` for a slot holding fewer units than
 *   asked.
 * @throws RangeError when the count or the size is not a whole number of at least 1.
 * @example
 * takeFromBag({ items: [1, 0], quantities: [3, 0] }, 0, 1, 2);
 * // { ok: true, bag: { items: [1, 0], quantities: [2, 0] }, slots: [0] }
 */
export function takeFromBag(bag: BagView, slot: number, count = 1, size = BAG_SLOTS): BagResult {
  requireCount(count, "count");
  requireCount(size, "size");
  if (!slotIn(slot, Math.max(size, bag.items.length))) {
    return { ok: false, reason: "range" };
  }
  const item = bag.items[slot] ?? 0;
  const quantity = item === 0 ? 0 : (bag.quantities[slot] ?? 0);
  if (quantity < count) {
    return { ok: false, reason: "empty" };
  }
  const next = copyOf(bag, 0);
  next.quantities[slot] = quantity - count;
  if (quantity === count) {
    next.items[slot] = 0;
  }
  return { ok: true, bag: next, slots: [slot] };
}

/**
 * Moves slot `from` onto slot `to`: onto an empty slot it moves, onto the same item it merges up to the stack size and
 * leaves the rest in `from`, onto another item it swaps. `from` may be a slot past the bag's size; `to` may not, and
 * a swap out of such a slot is refused, since it would land the other item past the size.
 *
 * @param bag The bag.
 * @param from The slot moved.
 * @param to The slot moved onto.
 * @param stack The stack size of the item in `from`.
 * @param size The bag's size, `BAG_SLOTS` unless given.
 * @returns The bag after the move and the two slots, or `range` for a slot outside the bag, `same` for one slot,
 *   `empty` for an empty `from`, or `full` for a merge onto a stack with no room and for a move from a slot past the
 *   size onto another item.
 * @throws RangeError when the stack or the size is not a whole number of at least 1.
 */
export function moveInBag(bag: BagView, from: number, to: number, stack: number, size = BAG_SLOTS): BagResult {
  requireCount(stack, "stack");
  requireCount(size, "size");
  if (!slotIn(from, Math.max(size, bag.items.length)) || !slotIn(to, size)) {
    return { ok: false, reason: "range" };
  }
  if (from === to) {
    return { ok: false, reason: "same" };
  }
  const moving = bag.items[from] ?? 0;
  if (moving === 0) {
    return { ok: false, reason: "empty" };
  }
  const next = copyOf(bag, size);
  const target = next.items[to] as number;
  const fromQuantity = next.quantities[from] as number;
  const toQuantity = next.quantities[to] as number;
  if (target === moving) {
    const moved = Math.min(fromQuantity, stack - toQuantity);
    if (moved <= 0) {
      return { ok: false, reason: "full" };
    }
    next.quantities[to] = toQuantity + moved;
    next.quantities[from] = fromQuantity - moved;
    if (fromQuantity === moved) {
      next.items[from] = 0;
    }
  } else {
    if (target !== 0 && from >= size) {
      return { ok: false, reason: "full" };
    }
    next.items[to] = moving;
    next.quantities[to] = fromQuantity;
    next.items[from] = target;
    next.quantities[from] = toQuantity;
  }
  return { ok: true, bag: next, slots: [from, to] };
}

/**
 * Splits a stack in half into the bag's first empty slot inside its size, in slot order (the author's controls of
 * 2026-10-08, ADR-091's note for P2.8-T14): the slot split keeps the larger half on an odd count, so 20 makes 10 and
 * 10 and 21 makes 11 and 10. The slot split may lie past the size; the new half never does.
 *
 * @param bag The bag.
 * @param slot The slot split.
 * @param size The bag's size, `BAG_SLOTS` unless given.
 * @returns The bag after the split and the two slots, the slot split first, or `range` for a slot outside the bag,
 *   `empty` for a slot holding fewer than 2 units, or `full` for a bag with no empty slot inside its size.
 * @throws RangeError when the size is not a whole number of at least 1.
 * @example
 * splitInBag({ items: [1, 0], quantities: [21, 0] }, 0, 2);
 * // { ok: true, bag: { items: [1, 1], quantities: [11, 10] }, slots: [0, 1] }
 */
export function splitInBag(bag: BagView, slot: number, size = BAG_SLOTS): BagResult {
  requireCount(size, "size");
  if (!slotIn(slot, Math.max(size, bag.items.length))) {
    return { ok: false, reason: "range" };
  }
  const item = bag.items[slot] ?? 0;
  const quantity = item === 0 ? 0 : (bag.quantities[slot] ?? 0);
  if (quantity < 2) {
    return { ok: false, reason: "empty" };
  }
  let target = -1;
  for (let each = 0; each < size; each += 1) {
    if ((bag.items[each] ?? 0) === 0) {
      target = each;
      break;
    }
  }
  if (target < 0) {
    return { ok: false, reason: "full" };
  }
  const next = copyOf(bag, size);
  const moved = Math.floor(quantity / 2);
  next.quantities[slot] = quantity - moved;
  next.items[target] = item;
  next.quantities[target] = moved;
  return { ok: true, bag: next, slots: [slot, target] };
}

/**
 * The orders a bag can be sorted in (ADR-125 decisions 3.1 and 3.8), as `bagSort` names them.
 */
export const BAG_SORT_ORDERS = ["type", "rarity", "name", "newest"] as const;
export type BagSortOrder = (typeof BAG_SORT_ORDERS)[number];

/**
 * What the sort reads of an item's record.
 */
export type SortedItem = Pick<Item, "id" | "name" | "kind" | "rarity" | "stack">;

/**
 * Each kind's place under Type and Rarity: the filter's order (ADR-125 decision 2.3), gear first.
 */
const KIND_PLACE: Readonly<Record<ItemKind, number>> = { equipment: 0, consumable: 1, material: 2, quest: 3 };

/**
 * Each rarity's place: rarest first, Relic before the ladder's top (ADR-125 decision 3.1).
 */
const RARITY_PLACE: Readonly<Record<ItemRarity, number>> = { relic: 0, legendary: 1, epic: 2, rare: 3, uncommon: 4, common: 5 };

/**
 * One id's pooled units, with the keys the orders compare.
 */
interface Pooled {
  readonly item: number;
  readonly record: SortedItem;
  readonly name: string;
  units: number;
  acquired: number;
}

/**
 * Compares two strings code unit by code unit, so every runtime agrees whatever its collation.
 *
 * @param a One.
 * @param b The other.
 * @returns Negative, zero or positive.
 */
function codeUnitOrder(a: string, b: string): number {
  return a < b ? -1 : a > b ? 1 : 0;
}

/**
 * Compares two pooled ids in an order: Type is kind, rarity, name, id; Rarity is rarity, kind, name, id; Name is
 * name, id; Newest is the time acquired, latest first and an id with no time last, then name, id.
 *
 * @param order The order.
 * @param a One.
 * @param b The other.
 * @returns Negative, zero or positive.
 */
function pooledOrder(order: BagSortOrder, a: Pooled, b: Pooled): number {
  const kind = KIND_PLACE[a.record.kind] - KIND_PLACE[b.record.kind];
  const rarity = RARITY_PLACE[a.record.rarity] - RARITY_PLACE[b.record.rarity];
  const first = order === "type" ? kind || rarity : order === "rarity" ? rarity || kind : order === "newest" ? b.acquired - a.acquired : 0;
  return first || codeUnitOrder(a.name, b.name) || codeUnitOrder(a.record.id, b.record.id);
}

/**
 * Sorts a bag (ADR-125 decisions 3.1 to 3.3 and 3.8), the same on the page and the map server, except that only the
 * map server holds the times Newest reads. The stacks of one id are pooled
 * and laid out again as full stacks of the record's size and one remainder, full stacks first, no unit created or
 * lost; then every id in the order, into the slots the sort may use in slot order, the empties falling to the end. It
 * leaves alone, in its slot, neither moved nor joined in or out: a kept stack, a slot holding an item `itemOf` does not
 * know (an id the rules lack), a stack larger than its record allows, and a slot past the size.
 *
 * @param bag The bag.
 * @param order Type, Rarity, Name or Newest.
 * @param itemOf The record of an item number, undefined for one the rules lack.
 * @param size The bag's size, `BAG_SLOTS` unless given.
 * @param kept Each slot's kept flag, non-zero for a kept stack; none kept unless given.
 * @param acquired When each slot's stack was acquired, in milliseconds, 0 for a time not held; none held unless given.
 *   Newest reads an id's time as the oldest of the stacks it pools, a stack with no time making the id oldest; the
 *   other orders never read it.
 * @returns The bag sorted and the slots whose content changed, in slot order; no slot when it was already sorted.
 * @throws RangeError when the size is not a whole number of at least 1.
 * @example
 * sortBag({ items: [0, 1, 1], quantities: [0, 12, 15] }, "name", (item) => records[item - 1], 3).bag;
 * // { items: [1, 1, 0], quantities: [20, 7, 0] } for a record of stack 20
 */
export function sortBag(bag: BagView, order: BagSortOrder, itemOf: (item: number) => SortedItem | undefined, size = BAG_SLOTS, kept?: ArrayLike<number>, acquired?: ArrayLike<number>): BagChange {
  requireCount(size, "size");
  const before = copyOf(bag, size);
  const free: number[] = [];
  const pools = new Map<number, Pooled>();
  for (let slot = 0; slot < size; slot += 1) {
    const item = before.items[slot] as number;
    const quantity = before.quantities[slot] as number;
    const record = item === 0 ? undefined : itemOf(item);
    if (item !== 0 && (record === undefined || quantity > record.stack || (kept?.[slot] ?? 0) !== 0)) {
      continue;
    }
    free.push(slot);
    if (record !== undefined) {
      const pool = pools.get(item);
      const time = acquired?.[slot] ?? 0;
      if (pool === undefined) {
        pools.set(item, { item, record, name: record.name.toLowerCase(), units: quantity, acquired: time });
      } else {
        pool.units += quantity;
        pool.acquired = Math.min(pool.acquired, time);
      }
    }
  }
  const next = copyOf(bag, size);
  let at = 0;
  for (const pool of [...pools.values()].sort((a, b) => pooledOrder(order, a, b))) {
    for (let left = pool.units; left > 0; at += 1) {
      const slot = free[at] as number;
      const quantity = Math.min(left, pool.record.stack);
      next.items[slot] = pool.item;
      next.quantities[slot] = quantity;
      left -= quantity;
    }
  }
  for (; at < free.length; at += 1) {
    const slot = free[at] as number;
    next.items[slot] = 0;
    next.quantities[slot] = 0;
  }
  const slots = free.filter((slot) => next.items[slot] !== before.items[slot] || next.quantities[slot] !== before.quantities[slot]);
  return { ok: true, bag: next, slots };
}
